<script setup lang="ts">
import {inject, onMounted, ref} from 'vue'
import {jobPrintoutsAPI as API} from '@/api/jobs/JobPrintouts'
import {capitalcase} from '@/utils/filters'
import {useI18n} from 'vue-i18n'
import Say from '@/utils/Say'
import sayErrorResponse from '@/mixins/sayErrorResponse'
import {useAuth} from '@/utils/auth'

const {t} = useI18n()
const auth = useAuth()

const jobId = inject('jobId', null) as any
API.setJobId(jobId.value)

const headers: any = ref([
  {title: t('createdAt'), key: 'created_at'},
  {title: t('proForma'), key: 'pro_forma_id'},
  {title: t('generatedBy'), key: 'generated_by'},
])

const items = ref<any[]>([])
const creating = ref(false)

onMounted(() => {
  fetchPrintouts()
})

const fetchPrintouts = () => {
  API.index()
    .then((response) => {
      items.value = response.data.data
    })
    .catch((error: any) => {
      sayErrorResponse(error.response?.data?.message ?? error.message)
    })
}

const createPrintout = () => {
  creating.value = true
  API.store()
    .then((response) => {
      items.value.unshift(response.data.data)
      Say('success', t('jobPrintoutCreateSuccess'))
    })
    .catch((error: any) => {
      sayErrorResponse(error.response?.data?.message ?? error.message)
    })
    .finally(() => {
      creating.value = false
    })
}

const openPrintout = async (printout: any) => {
  try {
    const response = await API.download(printout.id)
    const blob = new Blob([response.data], {type: 'application/pdf'})
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  } catch (error: any) {
    sayErrorResponse(error.response?.data?.message ?? error.message)
  }
}

const formatDate = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
}

const formatDateTime = (dateTime: string) => {
  const [date, time] = dateTime.split(' ');
  return `${formatDate(date)} ${time}`;
}
</script>

<template>
  <div>
    <v-row>
      <v-col>
        <v-btn
          v-if="auth.check('create job print-outs')"
          :text="t('jobPrintoutCreate')"
          :loading="creating"
          @click="createPrintout"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card flat rounded border>
          <v-data-table-server
            :headers="headers"
            :items="items"
            :items-length="items.length"
            :items-per-page="items.length"
            :hide-default-footer="true"
            disable-sort
          >
            <template v-slot:headers="{isSorted, getSortIcon, toggleSort}">
              <tr>
                <template v-for="header in headers" :key="header.key">
                  <th class="custom_header_table custom-bg" :style="{width: `${header.width}px !important`}">
                    <span class="d-flex align-center" @click="header.sortable ? toggleSort(header) : ''">
                      <span :class="header.sortable ? 'pointer' : ''" class="font-weight-bold">{{ capitalcase(header.title) }}</span>
                      <template v-if="isSorted(header)">
                        <v-icon :icon="getSortIcon(header)" size="small" style="margin: 5px"></v-icon>
                      </template>
                      <template v-if="header.sortable && !isSorted(header)">
                        <v-icon icon="mdi-sort" size="small" style="margin: 5px"></v-icon>
                      </template>
                    </span>
                  </th>
                </template>
              </tr>
            </template>

            <template #[`item.created_at`]="rowData">
              <span class="printout-link" @click="openPrintout(rowData.item)">{{ formatDateTime(rowData.item.created_at) }}</span>
            </template>

            <template #[`item.pro_forma_id`]="rowData">
              <template v-if="rowData.item.proForma">{{ rowData.item.proForma.number }}</template>
              <template v-else>&ndash;</template>
            </template>

            <template #[`item.generated_by`]="rowData">
              {{ rowData.item.generated_by ? rowData.item.generated_by.name : t('jobPrintoutAutoGenerated') }}
            </template>

          </v-data-table-server>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-table td) {
  white-space: nowrap;
}
.printout-link {
  cursor: pointer;
  color: rgb(25, 118, 210);
  text-decoration: underline;
}
</style>
